import type { JobRun, PappersEndpointStats, PappersJobStats } from "@/api/types";

type ReportedNumber = number | null | undefined;

export interface PappersMetric {
  key: string;
  label: string;
  value: ReportedNumber;
  note?: string;
}

export interface PappersStatsView {
  isMock: boolean;
  summary: PappersMetric[];
  endpoints: Array<{ name: string; metrics: PappersMetric[]; basis?: string }>;
  outcomeNotes: string[];
  notes: string[];
  otherStats: Record<string, unknown>;
}

const endpointNumbers = [
  "attempts", "repeat_attempts", "error_retries", "responses_received",
  "response_receipt_unknown", "requested_results", "received_results_known_subtotal",
  "results_unknown_attempts", "estimated_credits", "estimated_credits_known_subtotal",
  "estimated_credits_unknown_attempts", "confirmed_credits",
] as const;

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function number(value: unknown): ReportedNumber {
  if (value === null) return null;
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function string(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function endpoint(value: unknown): PappersEndpointStats | undefined {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
  const raw = record(value);
  const result: PappersEndpointStats = {};
  for (const key of endpointNumbers) result[key] = number(raw[key]);
  result.estimate_basis = string(raw.estimate_basis);
  result.is_mock = typeof raw.is_mock === "boolean" ? raw.is_mock : undefined;
  return result;
}

/** Prefer canonical endpoint values over aliases without falling through null.
 * Never coerce strings, booleans, missing fields or non-finite values to zero. */
function reported(...values: unknown[]): ReportedNumber {
  return number(values.find((value) => value !== undefined));
}

function readStats(raw: Record<string, unknown>): PappersJobStats {
  const endpoints = record(raw.endpoints);
  const hydration = record(raw.hydration);
  return {
    returned: reported(record(endpoints["/recherche"]).received_results_known_subtotal, raw.returned),
    created: reported(raw.created, hydration.created),
    refreshed: number(raw.refreshed),
    estimated_search_credits: reported(record(endpoints["/recherche"]).estimated_credits, raw.estimated_search_credits, raw.search_credits),
    estimated_detail_credits: reported(record(endpoints["/entreprise"]).estimated_credits, raw.estimated_detail_credits, raw.credits_spent, hydration.detail_credits),
    confirmed_credits: number(raw.confirmed_credits),
    budget_credits: number(raw.budget_credits),
    credit_basis: string(raw.credit_basis),
    credit_accounting: string(raw.credit_accounting),
    budget_scope: string(raw.budget_scope),
    is_mock: typeof raw.is_mock === "boolean" ? raw.is_mock : undefined,
    endpoints: {
      "/recherche": endpoint(endpoints["/recherche"]),
      "/entreprise": endpoint(endpoints["/entreprise"]),
    },
    hydration: {
      credits_saved_by_summary_exclusions: number(hydration.credits_saved_by_summary_exclusions),
    },
  };
}

export function formatPappersNumber(value: ReportedNumber): string {
  if (value === null) return "Unknown";
  if (value === undefined || !Number.isFinite(value)) return "Not reported";
  // Significant digits keep small, nonzero fractional estimates from displaying 0.
  return value.toLocaleString("en-US", { maximumSignificantDigits: 15 });
}

function endpointMetrics(stats: PappersEndpointStats, search: boolean): PappersMetric[] {
  const metrics: PappersMetric[] = [
    { key: "attempts", label: "Reserved call attempts", value: stats.attempts, note: "Includes failures and repeats; a reservation does not confirm an HTTP response." },
    { key: "responses_received", label: "HTTP responses received", value: stats.responses_received },
    { key: "estimated_credits", label: "Estimated credits", value: stats.estimated_credits },
    { key: "confirmed_credits", label: "Confirmed debits (credits)", value: stats.confirmed_credits },
  ];
  const optional: Array<[keyof PappersEndpointStats, string]> = [
    ["repeat_attempts", "Repeated attempts"],
    ["error_retries", "Retries after errors"],
    ["response_receipt_unknown", "Attempts with unknown response receipt"],
    ["estimated_credits_known_subtotal", "Estimated credits — known subtotal"],
    ["estimated_credits_unknown_attempts", "Attempts with unknown estimated cost"],
  ];
  if (search) optional.push(
    ["requested_results", "Requested search capacity (rows)"],
    ["received_results_known_subtotal", "Received search results — known subtotal"],
    ["results_unknown_attempts", "Attempts with unknown result count"],
  );
  for (const [key, label] of optional) {
    const value = number(stats[key]);
    if (value !== undefined) metrics.push({
      key, label, value,
      note: key.endsWith("known_subtotal") ? "Known values only; excludes unknown or unreported values." : undefined,
    });
  }
  return metrics;
}

/** Job ids, not the project type or arbitrary credit-looking keys, select this
 * presentation. No cross-endpoint/cross-run sums: endpoint known subtotals are
 * supplied by the backend and never promoted to totals or provider debits. */
export function getPappersStatsView(run: Pick<JobRun, "job_id" | "stats">): PappersStatsView | null {
  if (run.job_id !== "ma_enrichment" && run.job_id !== "ma_refresh") return null;
  const raw = record(run.stats);
  const stats = readStats(raw);
  const refresh = run.job_id === "ma_refresh";
  const summary: PappersMetric[] = refresh ? [
    { key: "refreshed", label: "Companies refreshed", value: stats.refreshed },
    { key: "detail_credits", label: "Estimated refresh credits", value: stats.estimated_detail_credits },
  ] : [
    {
      key: "returned",
      label: stats.endpoints?.["/recherche"]?.received_results_known_subtotal !== undefined
        ? "Search results — known subtotal" : "Search results received (reported)",
      value: stats.returned,
    },
    { key: "created", label: "New companies", value: stats.created },
    { key: "search_credits", label: "Estimated search credits", value: stats.estimated_search_credits },
    { key: "detail_credits", label: "Estimated detail credits", value: stats.estimated_detail_credits },
  ];
  summary.push({ key: "confirmed_credits", label: "Confirmed debits (credits)", value: stats.confirmed_credits });
  const endpoints = Object.entries(stats.endpoints ?? {}).flatMap(([key, value]) => value ? [{
    name: key === "/recherche" ? "Search · /recherche" : "Company detail · /entreprise",
    metrics: endpointMetrics(value, key === "/recherche"),
    basis: value.estimate_basis,
  }] : []);
  const notes = [
    "Estimates are not provider-confirmed debits. Unknown means explicitly unknown; Not reported means unavailable in this response.",
    "Run and project-period limits, including shared project-type limits, are not an account-wide Pappers budget.",
  ];
  const outcomeNotes: string[] = [];
  for (const [key, label] of [
    ["status", "Run result"], ["stop_reason", "Stop reason"], ["reason", "Reason"],
    ["budget_limit", "Limit reached"], ["error", "Error"],
  ]) {
    const value = string(raw[key]);
    if (value) outcomeNotes.push(`${label}: ${value}`);
  }
  if (stats.credit_basis || stats.credit_accounting) notes.push(`Estimate basis: ${stats.credit_basis ?? stats.credit_accounting}`);
  if (stats.budget_scope) notes.push(`Reported limit scope: ${stats.budget_scope}`);
  if (stats.budget_credits !== undefined) notes.push(`Run selection allowance (estimated credits): ${formatPappersNumber(stats.budget_credits)}`);
  const saved = stats.hydration?.credits_saved_by_summary_exclusions;
  if (saved !== undefined) notes.push(`Estimated detail credits avoided by summary exclusions: ${formatPappersNumber(saved)}`);

  const handled = new Set([
    "returned", "created", "refreshed", "search_credits", "credits_spent",
    "estimated_search_credits", "estimated_detail_credits", "confirmed_credits",
    "endpoints", "hydration", "is_mock", "credit_basis", "credit_accounting",
    "budget_credits", "budget_scope", "status", "stop_reason", "reason", "budget_limit", "error",
  ]);
  const otherStats = Object.fromEntries(Object.entries(raw).filter(([key]) => !handled.has(key)));
  const hydration = Object.fromEntries(Object.entries(record(raw.hydration)).filter(([key]) => !["created", "detail_credits", "credits_saved_by_summary_exclusions"].includes(key)));
  if (Object.keys(hydration).length) otherStats.hydration = hydration;
  return {
    isMock: stats.is_mock === true || Object.values(stats.endpoints ?? {}).some(value => value?.is_mock === true),
    summary, endpoints, outcomeNotes, notes, otherStats,
  };
}
